import { NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModules } from '../context/ModuleContext';
import { brandingAPI } from '../api';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { ScrollArea } from '../components/ui/scroll-area';
import { useState, useEffect } from 'react';
import { NotificationBell, ThemeToggle, HelpButton, CustomerPortalNotifications } from '../components/HeaderWidgets';
import { LiveChatPopup } from '../components/LiveChatPopup';
import { BugTracker } from '../components/BugTracker';
import { SmartSchedulingAssistant } from '../components/SmartSchedulingAssistant';
import { AgentChatWidget } from '../components/AgentChatWidget';
import { StaffActivityNotifier } from '../components/StaffActivityNotifier';
import {
  Building2, LayoutDashboard, Users, Building, HandshakeIcon,
  Target, Megaphone, TicketIcon, ShieldCheck, ListTodo, Settings, MapPin, LifeBuoy,
  LogOut, ChevronDown, ChevronRight, Package, Menu, X, Zap, Database, ShoppingCart, MessageSquareText, Mail, FileText, Palette, BarChart3, PenLine, Calendar, GitBranch, Scale, Lightbulb, Settings2, FlaskConical, Image, Layers, Brush, TrendingUp, FileBarChart, Search, Archive, Clock, Globe, Folder, UserCheck, DollarSign, PieChart, Workflow, Phone, Wallet, Headphones, Bot, Shield, Key, Wrench, Briefcase, Grid3X3, ExternalLink, FolderOpen, BookOpen, Bug, Brain, Crown, MessagesSquare
} from 'lucide-react';

// CRM submenu items - Now focused on customer activity overview
const crmSubItems = [
  { label: 'Contacts', path: '/contacts', icon: Users },
  { label: 'Companies', path: '/companies', icon: Building },
  { label: 'Tickets', path: '/tickets', icon: TicketIcon },
  { label: 'Orders', path: '/orders', icon: ShoppingCart },
];

// Dashboard submenu items - My Dashboard and Company Dashboard
// Company Dashboard is restricted to Admin, Manager, Executive, Board roles
const dashboardSubItems = [
  { label: 'My Dashboard', path: '/my-dashboard', icon: LayoutDashboard },
  { label: 'Calendar', path: '/calendar', icon: Calendar },
  { label: 'Company Dashboard', path: '/dashboard', icon: Building2, roles: ['admin', 'manager', 'executive', 'board', 'sales_manager', 'marketing_manager', 'finance_manager', 'hr_manager'] },
];

// Admin Portal submenu items
const adminPortalSubItems = [
  { label: 'Security', path: '/security', icon: Shield },
  { label: 'Legal', path: '/legal', icon: Scale },
  { label: 'Settings', path: '/settings', icon: Settings2 },
  { label: 'Bug Reports', path: '/bug-reports', icon: Bug },
  { label: 'System Docs', path: '/system-docs', icon: BookOpen },
  { label: 'Resources', path: '/resource-documents', icon: FileText },
  { label: 'Users', path: '/users', icon: Users },
  { label: 'Email Integration', path: '/email-settings', icon: Mail },
  { label: 'Email Signatures', path: '/email-signatures', icon: PenLine },
];

// Reports Section submenu items
const reportsSubItems = [
  { label: 'Reports', path: '/reports', icon: BarChart3 },
  { label: 'Manager Dashboard', path: '/manager-dashboard', icon: Target },
  { label: 'Board Dashboard', path: '/board-dashboard', icon: Shield },
  { label: 'Team Dashboard', path: '/team-dashboard', icon: Briefcase },
  { label: 'Dashboard Builder', path: '/dashboard-builder', icon: Grid3X3 },
];

// Marketing Hub submenu items (nested structure)
const marketingHubItems = [
  { label: 'Marketing Hub', path: '/marketing', icon: Megaphone },
  { label: 'Campaigns', path: '/marketing/campaigns', icon: Target },
  { label: 'Omni-Channel', path: '/marketing/omni-channel', icon: Globe },
  { label: 'Campaign Archives', path: '/marketing/campaign-archives', icon: Archive },
  { label: 'Compare Campaigns', path: '/marketing/compare-campaigns', icon: Scale },
  { label: 'Reporting', path: '/marketing/reporting', icon: BarChart3 },
  { label: 'AI Reports', path: '/marketing/ai-reports', icon: FileBarChart },
  { label: 'Content Calendar', path: '/marketing/calendar', icon: Calendar },
  { label: 'Workflows', path: '/marketing/workflows', icon: GitBranch },
  { label: 'Automation', path: '/marketing/automation', icon: Zap },
];

// Creative Hub submenu items
const creativeHubItems = [
  { label: 'Creative Hub', path: '/marketing/creative', icon: Brush },
  { label: 'Landing Pages', path: '/marketing/landing-pages', icon: Layers },
  { label: 'LP Analytics', path: '/marketing/landing-page-analytics', icon: TrendingUp },
  { label: 'Asset Library', path: '/marketing/asset-repository', icon: Image },
  { label: 'Templates', path: '/marketing/templates', icon: Layers },
  { label: 'SEO Tools', path: '/marketing/seo-tools', icon: Search },
  { label: 'A/B Testing', path: '/marketing/ab-testing', icon: FlaskConical },
  { label: 'Ideation Board', path: '/marketing/ideation', icon: Lightbulb },
];

// Reporting submenu items
const reportingItems = [
  { label: 'Marketing Reports', path: '/marketing/marketing-reports', icon: FileText },
  { label: 'Scheduled Reports', path: '/marketing/scheduled-reports', icon: Clock },
  { label: 'Executive Dashboard', path: '/executive-dashboard', icon: BarChart3 },
];

// Settings submenu items (within Marketing)
const marketingSettingsItems = [
  { label: 'Design Manager', path: '/marketing/design-manager', icon: Palette },
  { label: 'Brand Voice', path: '/marketing/brand-voice', icon: Palette },
  { label: 'Branding', path: '/settings/branding', icon: Brush },
  { label: 'Customer Segments', path: '/settings/segments', icon: Target },
  { label: 'Legal Rules', path: '/marketing/legal-rules', icon: Scale, adminOnly: true },
  { label: 'Social Media', path: '/settings/social-media', icon: MessageSquareText },
];

// Combined Marketing Suite with nested structure
const marketingSubItems = [
  { 
    label: 'Marketing Hub', 
    icon: Megaphone,
    isNested: true,
    subItems: marketingHubItems
  },
  { 
    label: 'Creative Hub', 
    icon: Brush,
    isNested: true,
    subItems: creativeHubItems
  },
  { 
    label: 'Reporting', 
    icon: BarChart3,
    isNested: true,
    subItems: reportingItems
  },
  { 
    label: 'Settings', 
    icon: Settings,
    isNested: true,
    subItems: marketingSettingsItems
  },
];

// Email Templates submenu items (keeping for backward compatibility)
const automationSubItems = [
  { label: 'Email Sequences', path: '/marketing/automation', icon: Zap },
  { label: 'Engagement', path: '/engagement', icon: Mail },
  { label: 'Email Templates', path: '/email-templates', icon: FileText },
];

// Commerce submenu items (nested under Sales)
const commerceSubItems = [
  { label: 'Overview', path: '/sales/commerce', icon: LayoutDashboard },
  { label: 'Products & Services', path: '/sales/commerce/products', icon: Package },
  { label: 'Warranties', path: '/sales/commerce/warranties', icon: ShieldCheck },
  { label: 'Documentation', path: '/sales/commerce/documentation', icon: Folder },
];

// Sales submenu items
const salesSubItems = [
  { label: 'Dashboard', path: '/sales', icon: LayoutDashboard },
  { label: 'Sales Workspace', path: '/sales/workspace', icon: LayoutDashboard },
  { label: 'Market Research', path: '/sales/market-research', icon: Search },
  { label: 'Campaigns', path: '/sales/campaigns', icon: Target },
  { label: 'Playbooks', path: '/sales/playbooks', icon: BookOpen },
  { label: 'Deals', path: '/sales/deals', icon: HandshakeIcon },
  { label: 'Quotes', path: '/sales/quotes', icon: FileText },
  { label: 'Pipeline', path: '/sales/pipeline', icon: TrendingUp },
  { label: 'Forecasting', path: '/sales/forecasting', icon: PieChart },
  { label: 'Email Sequences', path: '/sales/email-sequences', icon: Mail },
  { label: 'Prospecting', path: '/sales/prospecting', icon: Phone },
  { label: 'Lead Sources', path: '/sales/lead-sources', icon: Globe },
  { label: 'AI Insights', path: '/sales/ai-insights', icon: Zap },
  { label: 'Marketing Requests', path: '/sales/marketing-requests', icon: Megaphone },
  { label: 'Affiliates', path: '/sales/affiliates', icon: Users },
  { 
    label: 'Commerce', 
    icon: ShoppingCart,
    isNested: true,
    subItems: commerceSubItems
  },
  { label: 'Workflow Rules', path: '/sales/workflow-rules', icon: Workflow },
];

// Tools submenu items
const toolsSubItems = [
  { label: 'Communications Hub', path: '/communications', icon: MessagesSquare },
  { label: 'Live Chat', path: '/live-chat/dashboard', icon: MessageSquareText },
  { label: 'Chat Analytics', path: '/live-chat/analytics', icon: BarChart3 },
  { label: 'Chat Settings', path: '/live-chat/settings', icon: Settings },
  { label: 'Embed Widget', path: '/live-chat/embed', icon: FileText },
  { label: 'VoIP Settings', path: '/settings/voip', icon: Phone, adminOnly: true },
  { label: 'Customer Portal', path: '/customer-portal-admin', icon: Users, adminOnly: true },
  { label: 'Data Hub', path: '/data-hub', icon: Database },
  { label: 'CRM Settings', path: '/settings/crm', icon: Settings2 },
  { label: 'Module Config', path: '/settings/modules', icon: Settings2, adminOnly: true },
];

const navItems = [
  { 
    label: 'Dashboard',
    icon: LayoutDashboard,
    isDropdown: true,
    roles: ['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support'],
    subItems: dashboardSubItems
  },
  { 
    label: 'CRM',
    icon: Database,
    isDropdown: true,
    module: 'crm',
    roles: ['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support'],
    subItems: crmSubItems
  },
  {
    label: 'Marketing',
    icon: Megaphone,
    isDropdown: true,
    module: 'marketing',
    roles: ['admin', 'marketing_manager'],
    subItems: marketingSubItems
  },
  { 
    label: 'Sales',
    icon: DollarSign,
    isDropdown: true,
    module: 'sales',
    roles: ['admin', 'sales_rep', 'sales_manager'],
    subItems: salesSubItems
  },
  { 
    label: 'Finance Hub',
    icon: Wallet,
    isDropdown: true,
    module: 'finance',
    roles: ['admin', 'sales_manager', 'finance_manager'],
    subItems: [
      { label: 'Finance Dashboard', path: '/finance', icon: LayoutDashboard },
      { label: 'Accounts Receivable', path: '/finance/accounts-receivable', icon: DollarSign },
      { label: 'Budget Templates', path: '/finance/budget-templates', icon: FileText },
      { label: 'Variance Alerts', path: '/finance/variance-alerts', icon: TrendingUp },
      { label: 'Cash Flow', path: '/finance/cash-flow', icon: Wallet },
      { label: 'Reports', path: '/finance/reports', icon: BarChart3 }
    ]
  },
  { 
    label: 'Service Hub',
    icon: Headphones,
    isDropdown: true,
    module: 'service',
    roles: ['admin', 'sales_manager', 'support_agent', 'technical_support'],
    subItems: [
      { label: 'Service Dashboard', path: '/service-hub', icon: LayoutDashboard },
      { label: 'Help Desk', path: '/service-hub/help-desk', icon: Headphones },
      { label: 'Knowledge Base', path: '/service-hub/knowledge-base', icon: BookOpen },
      { label: 'AI Library', path: '/service-hub/ai-library', icon: Brain },
      { label: 'Customer Success', path: '/service-hub/customer-success', icon: UserCheck },
      { label: 'Surveys', path: '/service-hub/surveys', icon: FileText },
      { label: 'Warranty Map', path: '/warranty-regions', icon: MapPin },
      { label: 'Portal Analytics', path: '/portal-analytics', icon: BarChart3 },
      { label: 'Send Notification', path: '/send-notification', icon: Megaphone },
      { label: 'Customer Resources', path: '/customer-resources', icon: LifeBuoy },
      { label: 'Reports', path: '/service-hub/reports', icon: BarChart3 }
    ]
  },
  { 
    label: 'HR Hub',
    icon: Users,
    isDropdown: true,
    module: 'hr',
    roles: ['admin', 'hr_manager', 'sales_manager'],
    subItems: [
      { label: 'HR Dashboard', path: '/hr-hub', icon: LayoutDashboard },
      { label: 'AI Workforce', path: '/hr-hub/ai-workforce', icon: Bot },
      { label: 'AI Workspace', path: '/ai-workspace', icon: Brain },
      { label: 'Team Mgmt', path: '/team-management', icon: Users },
      { label: 'Holiday Calendar', path: '/holiday-settings', icon: Calendar }
    ]
  },
  { 
    label: 'Operations Hub',
    icon: Shield,
    isDropdown: true,
    module: 'operations',
    roles: ['admin', 'sales_rep', 'sales_manager', 'marketing_manager', 'support_agent', 'technical_support', 'team_lead'],
    subItems: [
      { label: 'Operations Dashboard', path: '/operations', icon: LayoutDashboard },
      { label: 'Leadership AI Hub', path: '/ai/leadership', icon: Crown },
      { label: 'File Library', path: '/files', icon: FolderOpen },
      { label: 'My Settings', path: '/my-settings', icon: Settings },
      { label: 'Internal Memos', path: '/memos', icon: MessageSquareText },
      { label: 'Workflow Management', path: '/workflow-management', icon: Workflow }
    ]
  },
  { 
    label: 'Tools',
    icon: Wrench,
    isDropdown: true,
    module: 'tools',
    roles: ['admin', 'sales_rep', 'support_agent', 'technical_support'],
    subItems: toolsSubItems
  },
  { 
    label: 'Reports',
    icon: BarChart3,
    isDropdown: true,
    module: 'reports',
    roles: ['admin', 'sales_rep', 'sales_manager', 'marketing_manager', 'support_agent', 'technical_support', 'team_lead', 'hr_manager'],
    subItems: reportsSubItems
  },
  { 
    label: 'Admin Portal',
    icon: Key,
    isDropdown: true,
    roles: ['admin'],
    subItems: adminPortalSubItems
  },
];

export const DashboardLayout = () => {
  const { user, logout, hasRole } = useAuth();
  const { isModuleEnabled, loading: modulesLoading, effectiveModules } = useModules();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [branding, setBranding] = useState(null);
  
  // Section collapse states for organized navigation
  const [coreModulesOpen, setCoreModulesOpen] = useState(true);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [adminSectionOpen, setAdminSectionOpen] = useState(false);
  
  // Dashboard dropdown state
  const [dashboardOpen, setDashboardOpen] = useState(
    location.pathname === '/dashboard' || location.pathname === '/my-dashboard'
  );
  const [crmOpen, setCrmOpen] = useState(
    crmSubItems.some(item => location.pathname.startsWith(item.path))
  );
  const [marketingOpen, setMarketingOpen] = useState(
    location.pathname.startsWith('/marketing') || location.pathname.startsWith('/executive-dashboard')
  );
  const [salesOpen, setSalesOpen] = useState(
    location.pathname.startsWith('/sales') || location.pathname.startsWith('/deals')
  );
  const [automationOpen, setAutomationOpen] = useState(
    automationSubItems.some(item => location.pathname.startsWith(item.path))
  );
  const [hrOpen, setHrOpen] = useState(
    location.pathname.startsWith('/hr-hub') || location.pathname.startsWith('/team-management') || location.pathname.startsWith('/holiday-settings')
  );
  const [operationsOpen, setOperationsOpen] = useState(
    location.pathname.startsWith('/operations') || location.pathname.startsWith('/my-settings') || 
    location.pathname.startsWith('/memos') || location.pathname.startsWith('/workflow-management')
  );
  const [financeOpen, setFinanceOpen] = useState(
    location.pathname.startsWith('/finance')
  );
  const [serviceOpen, setServiceOpen] = useState(
    location.pathname.startsWith('/service-hub')
  );
  const [toolsOpen, setToolsOpen] = useState(
    location.pathname.startsWith('/live-chat') || location.pathname.startsWith('/settings/crm')
  );
  // Reports dropdown state
  const [reportsOpen, setReportsOpen] = useState(
    location.pathname === '/reports' || location.pathname === '/manager-dashboard' || 
    location.pathname === '/board-dashboard' || location.pathname === '/team-dashboard' ||
    location.pathname === '/dashboard-builder'
  );
  // Admin Portal dropdown state
  const [adminPortalOpen, setAdminPortalOpen] = useState(
    location.pathname === '/security' || location.pathname === '/legal' || 
    location.pathname === '/settings' || location.pathname === '/resource-documents' ||
    location.pathname === '/users' || location.pathname === '/email-settings' ||
    location.pathname === '/email-signatures'
  );
  
  // Nested menu states
  const [nestedMenuStates, setNestedMenuStates] = useState({
    'Marketing Hub': location.pathname.startsWith('/marketing/campaign') || location.pathname === '/marketing' || location.pathname.startsWith('/marketing/omni') || location.pathname.startsWith('/marketing/report') || location.pathname.startsWith('/marketing/calendar') || location.pathname.startsWith('/marketing/workflow') || location.pathname.startsWith('/marketing/automation'),
    'Creative Hub': location.pathname.startsWith('/marketing/creative') || location.pathname.startsWith('/marketing/landing') || location.pathname.startsWith('/marketing/asset') || location.pathname.startsWith('/marketing/template') || location.pathname.startsWith('/marketing/seo') || location.pathname.startsWith('/marketing/ab') || location.pathname.startsWith('/marketing/ideation'),
    'Reporting': location.pathname.startsWith('/marketing/marketing-reports') || location.pathname.startsWith('/marketing/scheduled') || location.pathname.startsWith('/executive'),
    'Settings': location.pathname.startsWith('/marketing/design') || location.pathname.startsWith('/marketing/brand') || location.pathname.startsWith('/marketing/legal') || location.pathname.startsWith('/settings/social'),
    'Commerce': location.pathname.startsWith('/sales/commerce'),
  });
  
  const toggleNestedMenu = (label) => {
    setNestedMenuStates(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const res = await brandingAPI.get();
      setBranding(res.data);
    } catch (error) {
      console.log('Using default branding');
    }
  };

  const companyName = branding?.company_name || 'UnifiCRM';
  
  // Helper to get full URL for uploaded assets
  const getAssetUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    // Convert /uploads/ to /api/uploads/ for proper routing through ingress
    const apiPath = path.startsWith('/uploads/') ? `/api${path}` : path;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}${apiPath}`;
  };
  
  const logoUrl = getAssetUrl(branding?.logo_url);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter nav items by role AND module configuration
  // When modules are loading or empty, show all items (fallback behavior)
  const filteredNavItems = navItems.filter(item => {
    // First check role access
    if (!hasRole(item.roles)) return false;
    
    // If modules are loading or not configured, show all items
    if (modulesLoading || effectiveModules.length === 0) return true;
    
    // Then check module access (if item has a module property)
    if (item.module && !isModuleEnabled(item.module)) return false;
    
    return true;
  });

  const getInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  const roleLabels = {
    admin: 'Administrator',
    sales_rep: 'Sales Representative',
    marketing_manager: 'Marketing Manager',
    support_agent: 'Support Agent',
    technical_support: 'Technical Support',
    customer: 'Customer'
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-layout">
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-40 flex items-center px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <div className="flex items-center gap-2 ml-4">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="h-6 object-contain" />
          ) : (
            <>
              <Building2 className="h-6 w-6" strokeWidth={1.5} />
              <span className="font-bold font-['Manrope']">{companyName}</span>
            </>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-30 h-full w-64 bg-slate-900 text-white
        transform transition-transform duration-200
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-8 object-contain" />
            ) : (
              <>
                <Building2 className="h-8 w-8" strokeWidth={1.5} />
                <span className="text-xl font-bold font-['Manrope']">{companyName}</span>
              </>
            )}
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-180px)]">
          <nav className="px-4 space-y-1">
            {filteredNavItems.map((item) => {
              // Determine which state to use for dropdowns
              const getDropdownState = () => {
                if (item.label === 'Dashboard') return { isOpen: dashboardOpen, setIsOpen: setDashboardOpen, subItemsToCheck: dashboardSubItems };
                if (item.label === 'CRM') return { isOpen: crmOpen, setIsOpen: setCrmOpen, subItemsToCheck: crmSubItems };
                if (item.label === 'Marketing') return { isOpen: marketingOpen, setIsOpen: setMarketingOpen, subItemsToCheck: [...marketingHubItems, ...creativeHubItems, ...reportingItems, ...marketingSettingsItems] };
                if (item.label === 'Sales') return { isOpen: salesOpen, setIsOpen: setSalesOpen, subItemsToCheck: salesSubItems };
                if (item.label === 'Automation') return { isOpen: automationOpen, setIsOpen: setAutomationOpen, subItemsToCheck: automationSubItems };
                if (item.label === 'HR Hub') return { isOpen: hrOpen, setIsOpen: setHrOpen, subItemsToCheck: item.subItems || [] };
                if (item.label === 'Operations Hub') return { isOpen: operationsOpen, setIsOpen: setOperationsOpen, subItemsToCheck: item.subItems || [] };
                if (item.label === 'Finance Hub') return { isOpen: financeOpen, setIsOpen: setFinanceOpen, subItemsToCheck: item.subItems || [] };
                if (item.label === 'Service Hub') return { isOpen: serviceOpen, setIsOpen: setServiceOpen, subItemsToCheck: item.subItems || [] };
                if (item.label === 'Tools') return { isOpen: toolsOpen, setIsOpen: setToolsOpen, subItemsToCheck: toolsSubItems };
                if (item.label === 'Reports') return { isOpen: reportsOpen, setIsOpen: setReportsOpen, subItemsToCheck: reportsSubItems };
                if (item.label === 'Admin Portal') return { isOpen: adminPortalOpen, setIsOpen: setAdminPortalOpen, subItemsToCheck: adminPortalSubItems };
                return { isOpen: false, setIsOpen: () => {}, subItemsToCheck: [] };
              };
              const { isOpen, setIsOpen, subItemsToCheck } = getDropdownState();
              
              // Filter subItems for admin-only items AND role-specific items
              const filteredSubItems = item.subItems?.filter(sub => {
                // Check adminOnly flag
                if (sub.adminOnly && !hasRole(['admin'])) return false;
                // Check item-specific roles if defined
                if (sub.roles && !hasRole(sub.roles)) return false;
                return true;
              }) || [];
              
              // Render nested submenu item
              const renderNestedSubItem = (subItem) => {
                if (subItem.isNested) {
                  const nestedOpen = nestedMenuStates[subItem.label];
                  const filteredNestedItems = subItem.subItems?.filter(sub => !sub.adminOnly || hasRole(['admin'])) || [];
                  const isNestedActive = filteredNestedItems.some(nested => location.pathname.startsWith(nested.path));
                  
                  return (
                    <Collapsible 
                      key={subItem.label} 
                      open={nestedOpen} 
                      onOpenChange={() => toggleNestedMenu(subItem.label)}
                    >
                      <CollapsibleTrigger asChild>
                        <button
                          className={`
                            w-full flex items-center justify-between gap-2 px-4 py-2 rounded-sm text-sm
                            transition-colors duration-150
                            ${isNestedActive
                              ? 'bg-white/10 text-white' 
                              : 'text-slate-400 hover:text-white hover:bg-white/5'
                            }
                          `}
                          data-testid={`nav-nested-${subItem.label.toLowerCase().replace(' ', '-')}`}
                        >
                          <div className="flex items-center gap-2">
                            <subItem.icon className="h-4 w-4" strokeWidth={1.5} />
                            <span className="font-medium">{subItem.label}</span>
                          </div>
                          {nestedOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pl-4 mt-1 space-y-0.5">
                        {filteredNestedItems.map((nestedItem) => (
                          <NavLink
                            key={nestedItem.path}
                            to={nestedItem.path}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) => `
                              flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs
                              transition-colors duration-150
                              ${isActive 
                                ? 'bg-white/10 text-white' 
                                : 'text-slate-500 hover:text-white hover:bg-white/5'
                              }
                            `}
                            data-testid={`nav-${nestedItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <nestedItem.icon className="h-3 w-3" strokeWidth={1.5} />
                            {nestedItem.label}
                          </NavLink>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  );
                }
                
                // Regular non-nested item
                return (
                  <NavLink
                    key={subItem.path}
                    to={subItem.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm
                      transition-colors duration-150
                      ${isActive 
                        ? 'bg-white/10 text-white' 
                        : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }
                    `}
                    data-testid={`nav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <subItem.icon className="h-4 w-4" strokeWidth={1.5} />
                    {subItem.label}
                  </NavLink>
                );
              };
              
              return item.isDropdown ? (
                <Collapsible key={item.label} open={isOpen} onOpenChange={setIsOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      className={`
                        w-full flex items-center justify-between gap-3 px-4 py-3 rounded-sm text-sm
                        transition-colors duration-150
                        ${subItemsToCheck.some(sub => location.pathname.startsWith(sub.path))
                          ? 'bg-white/10 text-white' 
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }
                      `}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="h-5 w-5" strokeWidth={1.5} />
                        {item.label}
                      </div>
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-2 mt-1 space-y-1">
                    {filteredSubItems.map(renderNestedSubItem)}
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-4 py-3 rounded-sm text-sm
                    transition-colors duration-150
                    ${isActive 
                      ? 'bg-white/10 text-white' 
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }
                  `}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="h-5 w-5" strokeWidth={1.5} />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User menu at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8 mr-3">
                  <AvatarFallback className="bg-slate-700 text-white text-xs">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-slate-500">{roleLabels[user?.role]}</p>
                </div>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.role === 'admin' && (
                <>
                  <DropdownMenuItem asChild>
                    <a href="/customer-login" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Preview Customer Portal
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Top bar with widgets */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-6 py-3 border-b border-border bg-card">
          <SmartSchedulingAssistant />
          <HelpButton />
          <BugTracker />
          <ThemeToggle variant="dropdown" />
          <CustomerPortalNotifications />
          <NotificationBell />
          <div className="h-6 w-px bg-border" />
          <span className="text-sm text-muted-foreground">
            {user?.first_name} {user?.last_name}
          </span>
        </div>
        
        {/* Mobile header widgets */}
        <div className="lg:hidden fixed top-0 right-4 z-50 flex items-center h-16 gap-2">
          <BugTracker />
          <ThemeToggle variant="icon" />
          <CustomerPortalNotifications />
          <NotificationBell />
        </div>
        
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
      
      {/* Live Chat Popup Widget */}
      <LiveChatPopup position="bottom-right" />
      
      {/* Agent Live Chat Widget - Floating */}
      <AgentChatWidget />

      {/* Out-of-tab desktop notifications + chime for new customer activity */}
      <StaffActivityNotifier />
    </div>
  );
};

export default DashboardLayout;
